import {
  type ButtonHTMLAttributes,
  type AnchorHTMLAttributes,
  forwardRef,
} from 'react';
import { cva } from 'class-variance-authority';
import { tlwTwMerge, type GenerateCvaType } from '@trg/utils';
import { Icon, type IconProps } from '../icon';

export type ButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type'
> &
  AnchorHTMLAttributes<HTMLAnchorElement> & {
    htmlType?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
    icon?: IconProps['name'];
    type?: 'default' | 'primary';
    size?: 'small' | 'medium' | 'large';
    iconPosition?: 'start' | 'end';
    iconSize?: IconProps['size'];
    dark?: boolean;
    outline?: boolean;
    link?: boolean;
    square?: boolean;
  };

type ButtonVariantsType = GenerateCvaType<
  ButtonProps,
  'type' | 'size' | 'iconPosition' | 'dark' | 'outline' | 'link' | 'square'
>;

const buttonVariants = cva<ButtonVariantsType>(
  [
    'tlw-txt-zh-btn tlw-border',
    'tlw-inline-flex tlw-justify-center tlw-items-center',
    'tlw-transition tlw-duration-300 tlw-select-none',
    'focus-visible:tlw-outline focus-visible:tlw-outline-2',
    'disabled:tlw-text-txt-white disabled:tlw-bg-gray-400 disabled:tlw-border-gray-400 disabled:tlw-cursor-not-allowed',
  ],
  {
    variants: {
      type: {
        default: [
          'tlw-text-txt-white tlw-bg-gray-800 tlw-border-gray-800 tlw-outline-black/30',
          'hover:tlw-text-txt-white hover:tlw-bg-pr hover:tlw-border-pr',
          'active:tlw-text-txt-white active:tlw-bg-pr active:tlw-border-pr',
        ],
        primary: [
          'tlw-text-txt-white tlw-bg-pr tlw-border-pr tlw-outline-[#ED77384D]',
          'hover:tlw-text-txt-white hover:tlw-bg-gray-800 hover:tlw-border-gray-800',
          'active:tlw-text-txt-white active:tlw-bg-gray-800 active:tlw-border-gray-800',
        ],
      },
      size: {
        small: 'tlw-px-2 tlw-py-1 tlw-gap-1 tlw-txt-zh-mini',
        medium: 'tlw-px-6 tlw-py-2 tlw-gap-2',
        large: 'tlw-px-6 tlw-py-2 tlw-gap-3 tlw-min-w-[132px] xl:tlw-min-w-40',
      },
      iconPosition: {
        start: 'tlw-flex-row',
        end: 'tlw-flex-row-reverse',
      },
      dark: {
        true: '',
        false: '',
      },
      outline: {
        true: '',
        false: '',
      },
      link: {
        true: [
          'tlw-underline tlw-underline-offset-2',
          'tlw-px-1 tlw-gap-1 tlw-border-none',
          'tlw-bg-transparent ',
          'hover:tlw-bg-transparent',
          'active:tlw-bg-transparent',
          'disabled:tlw-text-txt-500 disabled:tlw-bg-transparent',
        ],
        false: '',
      },
      square: {
        true: 'tlw-rounded',
        false: 'tlw-rounded-full',
      },
    },
    compoundVariants: [
      {
        type: 'default',
        dark: true,
        class: [
          'tlw-text-txt-800 tlw-bg-white tlw-border-white tlw-outline-[#ED77384D]',
        ],
      },
      {
        type: 'primary',
        dark: true,
        class: [
          'tlw-text-txt-800 tlw-bg-white tlw-border-white tlw-outline-black/30',
        ],
      },
      {
        type: 'default',
        outline: true,
        class: [
          'tlw-text-txt-800 tlw-bg-white tlw-border-gray-800 tlw-outline-black/25',
        ],
      },
      {
        type: 'primary',
        outline: true,
        class: [
          'tlw-text-txt-800 tlw-bg-white tlw-border-gray-800 tlw-outline-black/30',
        ],
      },
      {
        type: 'default',
        link: true,
        class: [
          'tlw-text-txt-700',
          'hover:tlw-text-txt-core',
          'active:tlw-text-txt-core',
        ],
      },
      {
        type: 'primary',
        link: true,
        class: [
          'tlw-text-txt-core',
          'hover:tlw-text-txt-800',
          'active:tlw-text-txt-800',
        ],
      },
    ],
  }
);

export const Button = forwardRef<
  HTMLButtonElement & HTMLAnchorElement,
  ButtonProps
>((props, ref) => {
  const {
    htmlType,
    icon,
    type = 'default',
    size = 'medium',
    iconPosition = 'start',
    iconSize,
    dark,
    outline,
    link,
    square = false,
    className,
    href,
    children,
    ...restProps
  } = props;

  const cls = tlwTwMerge(
    buttonVariants({
      type,
      size: link ? undefined : size,
      iconPosition,
      dark,
      outline,
      link,
      square: link ? true : square,
    }),
    className
  );

  const content = icon ? (
    <>
      {icon && (
        <Icon
          className="tlw-shrink-0"
          name={icon}
          size={iconSize ?? (link || size === 'small' ? 16 : 20)}
        />
      )}
      {children && <span>{children}</span>}
    </>
  ) : (
    children
  );

  if (href) {
    return (
      <a
        ref={ref}
        className={cls}
        href={href}
        children={content}
        {...restProps}
      />
    );
  } else {
    return (
      <button
        ref={ref}
        className={cls}
        type={htmlType}
        children={content}
        {...restProps}
      />
    );
  }
});
